import React from 'react';
import { PARTY_TYPES, USER_TYPES } from './constants';
import { newLoaLevel, newPerson, lesseeFromContact } from './payload';

/**
 * Editor for loa_detail — the signing/approval chain e-Contract runs the
 * contract through. Each level is one party (lessor, lessee, or a witness for
 * either) and lists the people who have to approve at that step. The API needs
 * either an email of someone who already has an e-Contract account
 * (user_type "user") or the person's details (user_type "none user").
 */
const LoaEditor = ({ levels, onChange, formData, econForm, locked }) => {
  const updateLevel = (id, patch) =>
    onChange(levels.map(l => (l.id === id ? { ...l, ...patch } : l)));

  const changeUserType = (level, userType) => {
    if (level.user_type === userType) return;
    // Carry the emails across so switching type doesn't throw away typing.
    const emails = level.members.map(m => m.email || '');
    const members = userType === 'user'
      ? (emails.length ? emails : ['']).map(email => ({ email }))
      : (emails.length ? emails : ['']).map(email => newPerson({ email }));
    updateLevel(level.id, { user_type: userType, members });
  };

  const updateMember = (level, index, patch) =>
    updateLevel(level.id, {
      members: level.members.map((m, i) => (i === index ? { ...m, ...patch } : m))
    });

  const addMember = (level) =>
    updateLevel(level.id, {
      members: [...level.members, level.user_type === 'user' ? { email: '' } : newPerson()]
    });

  const removeMember = (level, index) => {
    const members = level.members.filter((_, i) => i !== index);
    updateLevel(level.id, {
      members,
      min_approve_count: Math.min(parseInt(level.min_approve_count, 10) || 1, Math.max(members.length, 1))
    });
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= levels.length) return;
    const next = [...levels];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const addLevel = () => {
    const used = new Set(levels.map(l => l.contract_party_type));
    const party = ['lessor', 'lessee', 'lessor_witness', 'lessee_witness'].find(p => !used.has(p)) || 'lessee_witness';
    onChange([...levels, newLoaLevel(party, party.endsWith('witness') ? 'none user' : 'user')]);
  };

  const partyLabel = (value) => PARTY_TYPES.find(p => p.value === value)?.label || value;

  return (
    <div className="econ-loa">
      {levels.map((level, index) => (
        <div className="econ-loa__level" key={level.id}>
          <div className="econ-loa__level-head">
            <span className="econ-loa__level-no">ลำดับที่ {index + 1}</span>
            <span className="econ-loa__level-party">{partyLabel(level.contract_party_type)}</span>
            <span className="econ-loa__level-tools">
              <button type="button" className="econ-icon-btn" disabled={locked || index === 0} onClick={() => move(index, -1)} title="เลื่อนขึ้น">
                <i className="bi bi-arrow-up"></i>
              </button>
              <button type="button" className="econ-icon-btn" disabled={locked || index === levels.length - 1} onClick={() => move(index, 1)} title="เลื่อนลง">
                <i className="bi bi-arrow-down"></i>
              </button>
              <button
                type="button"
                className="econ-icon-btn econ-icon-btn--danger"
                disabled={locked}
                onClick={() => onChange(levels.filter(l => l.id !== level.id))}
                title="ลบลำดับนี้"
              >
                <i className="bi bi-trash"></i>
              </button>
            </span>
          </div>

          <div className="rental-form__grid">
            <div className="rental-form__field">
              <label>คู่สัญญา</label>
              <select
                className="rental-form__input"
                value={level.contract_party_type}
                disabled={locked}
                onChange={(e) => updateLevel(level.id, { contract_party_type: e.target.value })}
              >
                {PARTY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div className="rental-form__field">
              <label>ประเภทผู้ลงนาม</label>
              <select
                className="rental-form__input"
                value={level.user_type}
                disabled={locked}
                onChange={(e) => changeUserType(level, e.target.value)}
              >
                {USER_TYPES.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>

            <div className="rental-form__field">
              <label>จำนวนผู้อนุมัติขั้นต่ำ</label>
              <input
                type="number"
                min="1"
                max={Math.max(level.members.length, 1)}
                className="rental-form__input"
                value={level.min_approve_count}
                disabled={locked}
                onChange={(e) => updateLevel(level.id, { min_approve_count: e.target.value })}
              />
            </div>

            <div className="rental-form__field">
              <label>&nbsp;</label>
              <label className="econ-check">
                <input
                  type="checkbox"
                  checked={level.signature_required}
                  disabled={locked}
                  onChange={(e) => updateLevel(level.id, { signature_required: e.target.checked })}
                />
                ต้องลงลายมือชื่อดิจิทัล
              </label>
            </div>
          </div>

          <div className="econ-loa__members">
            {level.members.map((member, mIndex) => (
              <div className="econ-loa__member" key={mIndex}>
                {level.user_type === 'user' ? (
                  <div className="econ-loa__member-row">
                    <input
                      type="email"
                      className="rental-form__input"
                      placeholder="อีเมลผู้อนุมัติ (ต้องมีบัญชีใน e-Contract แล้ว)"
                      value={member.email || ''}
                      disabled={locked}
                      onChange={(e) => updateMember(level, mIndex, { email: e.target.value })}
                    />
                    <button type="button" className="econ-icon-btn econ-icon-btn--danger" disabled={locked} onClick={() => removeMember(level, mIndex)} title="ลบ">
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="econ-loa__person">
                      <input className="rental-form__input" placeholder="ชื่อ *" value={member.first_name || ''} disabled={locked} onChange={(e) => updateMember(level, mIndex, { first_name: e.target.value })} />
                      <input className="rental-form__input" placeholder="นามสกุล *" value={member.last_name || ''} disabled={locked} onChange={(e) => updateMember(level, mIndex, { last_name: e.target.value })} />
                      <input type="email" className="rental-form__input" placeholder="อีเมล *" value={member.email || ''} disabled={locked} onChange={(e) => updateMember(level, mIndex, { email: e.target.value })} />
                      <input className="rental-form__input" placeholder="ตำแหน่ง" value={member.job_position || ''} disabled={locked} onChange={(e) => updateMember(level, mIndex, { job_position: e.target.value })} />
                      <input className="rental-form__input" placeholder="เบอร์โทร" maxLength="10" value={member.phone_number || ''} disabled={locked} onChange={(e) => updateMember(level, mIndex, { phone_number: e.target.value.replace(/\D/g, '') })} />
                      <button type="button" className="econ-icon-btn econ-icon-btn--danger" disabled={locked} onClick={() => removeMember(level, mIndex)} title="ลบ">
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                    {level.contract_party_type === 'lessee' && (
                      <button
                        type="button"
                        className="econ-link-btn"
                        disabled={locked}
                        onClick={() => updateMember(level, mIndex, lesseeFromContact(formData, econForm))}
                      >
                        <i className="bi bi-person-lines-fill"></i> ใช้ข้อมูลผู้ติดต่อจากขั้นตอนที่ 1
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
            <button type="button" className="econ-link-btn" disabled={locked} onClick={() => addMember(level)}>
              <i className="bi bi-plus-circle"></i> เพิ่มผู้อนุมัติในลำดับนี้
            </button>
          </div>
        </div>
      ))}

      <button type="button" className="rental-form__btn rental-form__btn--secondary" disabled={locked} onClick={addLevel}>
        <i className="bi bi-plus-lg me-2"></i>เพิ่มลำดับผู้ลงนาม (เช่น พยาน)
      </button>
    </div>
  );
};

export default LoaEditor;
